// Layouts/MediaLibrary/MediaLibrary.tsx
// Super admin Media Library page — upload once, reuse everywhere.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';
import CustomConfirmDialog from '../../components/CustomConfirmDialog';
import MediaBrowser, { MediaBrowserHandle } from './MediaBrowser';
import { MediaItem, MediaItemDetails, KIND_STYLE, formatMediaSize, formatMediaDate } from './mediaTypes';

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '11px', padding: '7px 0', borderBottom: '1px solid #E8EBF8', fontSize: '0.722rem' }}>
    <span style={{ color: '#5A6080', flexShrink: 0 }}>{label}</span>
    <span style={{ color: '#12183A', fontWeight: 500, textAlign: 'right', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</span>
  </div>
);

const MediaLibrary: React.FC = () => {
  const toast = useRef<Toast>(null);
  const browser = useRef<MediaBrowserHandle>(null);

  const [, setTotal] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const [details, setDetails] = useState<MediaItemDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadDetails = useCallback(async (id: string) => {
    setDetailsLoading(true);
    try {
      const res = await apiClient.get(`/v1/media/${id}`);
      if (res.data.success) {
        setDetails(res.data.data);
        setTitleDraft(res.data.data.title);
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Could not load file details', life: 4000 });
      setOpenId(null);
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (openId) { setDetails(null); loadDetails(openId); }
  }, [openId, loadDetails]);

  const handleRename = async () => {
    if (!details) return;
    const title = titleDraft.trim();
    if (!title || title === details.title) return;
    setSaving(true);
    try {
      const res = await apiClient.patch(`/v1/media/${details.id}`, { title });
      if (res.data.success) {
        setDetails((d) => (d ? { ...d, title: res.data.data.title } : d));
        browser.current?.patchItem(res.data.data as MediaItem);
        toast.current?.show({ severity: 'success', summary: 'Renamed', detail: 'Files already attached keep working.', life: 3000 });
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Could not rename', life: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!details) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/v1/media/${details.id}`);
      toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'File removed from the library and storage.', life: 3000 });
      setConfirmDelete(false);
      setOpenId(null);
      browser.current?.refresh();
    } catch (err: any) {
      setConfirmDelete(false);
      toast.current?.show({ severity: 'error', summary: 'Cannot delete', detail: err.response?.data?.error || 'Could not delete the file', life: 5000 });
      if (details) loadDetails(details.id);   // usage may have changed
    } finally {
      setDeleting(false);
    }
  };

  const copyUrl = async () => {
    if (!details) return;
    try {
      await navigator.clipboard.writeText(details.url);
      toast.current?.show({ severity: 'success', summary: 'Link copied', life: 1800 });
    } catch {
      toast.current?.show({ severity: 'warn', summary: 'Copy failed', detail: details.url, life: 5000 });
    }
  };

  const kind = details ? KIND_STYLE[details.type] || KIND_STYLE.other : KIND_STYLE.other;
  const inUse = (details?.usage.total ?? 0) > 0;

  return (
    <div style={{ padding: 0, margin: '0 auto', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <Toast ref={toast} />

      <CustomConfirmDialog
        visible={confirmDelete}
        onHide={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete File"
        message={`Delete "${details?.title ?? ''}" permanently from the library and storage? This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        cancelLabel="Cancel"
        severity="danger"
        icon="pi pi-trash"
      />

      <MediaBrowser ref={browser} mode="manage" onOpen={(item) => setOpenId(item.id)} activeId={openId} onTotalChange={setTotal} />

      {/* Details panel */}
      <Sidebar
        visible={!!openId}
        onHide={() => setOpenId(null)}
        position="right"
        showCloseIcon={false}
        style={{ width: '380px', maxWidth: '100vw' }}
        pt={{ content: { style: { padding: 0 } }, header: { style: { padding: 0, display: 'none' } } }}
      >
        <div style={{ background: 'linear-gradient(135deg, #14267A 0%, #2238A0 50%, #0B1650 100%)', padding: '19px 21px', color: 'white', position: 'relative' }}>
          <button
            type="button" onClick={() => setOpenId(null)} aria-label="Close"
            style={{ position: 'absolute', top: '14px', right: '14px', width: '28px', height: '28px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '7px', color: 'white', cursor: 'pointer' }}
          >
            <i className="pi pi-times" style={{ fontSize: '0.792rem' }} />
          </button>
          <div style={{ fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', opacity: 0.8, marginBottom: '5px' }}>File details</div>
          <h2 style={{ margin: 0, fontSize: '0.924rem', fontWeight: 700, paddingRight: '35px', wordBreak: 'break-word' }}>{details?.title ?? 'Loading…'}</h2>
        </div>

        <div style={{ padding: '18px 21px', background: '#F4F5FA', minHeight: 'calc(100% - 90px)' }}>
          {detailsLoading || !details ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '53px 0' }}><ProgressSpinner style={{ width: '32px', height: '32px' }} strokeWidth="3" /></div>
          ) : (
            <>
              {/* preview */}
              <div style={{ height: '176px', borderRadius: '12px', background: kind.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: '16px', border: '1px solid #E2E5F2' }}>
                {details.type === 'image'
                  ? <img src={details.url} alt={details.title} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  : <div style={{ textAlign: 'center', color: kind.color }}><i className={`pi ${kind.icon}`} style={{ fontSize: '2.992rem' }} /><div style={{ fontWeight: 700, marginTop: '7px' }}>{details.ext.toUpperCase()}</div></div>}
              </div>

              {/* rename */}
              <div style={{ background: 'white', border: '1px solid #E2E5F2', borderRadius: '12px', padding: '14px', marginBottom: '14px' }}>
                <GlobalInputField label="Display name" value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} maxLength={200} />
                <div style={{ marginTop: '9px' }}>
                  <GlobalButton label={saving ? 'Saving…' : 'Save name'} variant="secondary" icon="pi pi-check" onClick={handleRename}
                    disabled={saving || !titleDraft.trim() || titleDraft.trim() === details.title} fullWidth />
                </div>
                <div style={{ fontSize: '0.634rem', color: '#9298B5', marginTop: '7px' }}>
                  Only the name shown here changes. The file link stays the same, so nothing already attached breaks.
                </div>
              </div>

              {/* info */}
              <div style={{ background: 'white', border: '1px solid #E2E5F2', borderRadius: '12px', padding: '5px 14px', marginBottom: '14px' }}>
                <Row label="Type">{kind.label} (.{details.ext})</Row>
                <Row label="Size">{formatMediaSize(details.size)}</Row>
                <Row label="Added">{formatMediaDate(details.created_at)}</Row>
                <Row label="Source">{details.source === 'backfill' ? 'Existing upload (imported)' : 'Uploaded here'}</Row>
              </div>

              <div style={{ display: 'flex', gap: '9px', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}><GlobalButton label="Copy link" variant="outline" icon="pi pi-link" onClick={copyUrl} fullWidth /></div>
                <div style={{ flex: 1 }}><GlobalButton label="Open" variant="outline" icon="pi pi-external-link" onClick={() => window.open(details.url, '_blank', 'noopener')} fullWidth /></div>
              </div>

              {/* usage */}
              <div style={{ background: 'white', border: '1px solid #E2E5F2', borderRadius: '12px', padding: '14px', marginBottom: '14px' }}>
                <div style={{ fontSize: '0.634rem', fontWeight: 700, color: '#5A6080', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '9px' }}>
                  Used in ({details.usages.length})
                </div>
                {details.usages.length === 0 ? (
                  <div style={{ fontSize: '0.722rem', color: '#9298B5' }}>Not attached to any material yet.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                    {details.usages.map((u) => (
                      <div key={`${u.type}-${u.id}`} style={{ padding: '9px 11px', background: '#F4F5FA', border: '1px solid #E2E5F2', borderRadius: '9px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '7px' }}>
                          <span style={{ fontSize: '0.722rem', fontWeight: 600, color: '#12183A' }}>{u.title}</span>
                          <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 7px', borderRadius: '999px', textTransform: 'uppercase', background: u.type === 'study' ? '#eef2ff' : '#fff7ed', color: u.type === 'study' ? '#4338ca' : '#c2410c' }}>
                            {u.type === 'study' ? 'Study' : 'Practical'}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.634rem', color: '#5A6080', marginTop: '4px' }}>
                          {[u.course_title, u.module_title, u.chapter_title].filter(Boolean).join(' › ') || 'Whole course'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <GlobalButton label="Delete permanently" variant="danger" icon="pi pi-trash" onClick={() => setConfirmDelete(true)} disabled={inUse || deleting} fullWidth />
              {inUse && (
                <div style={{ fontSize: '0.634rem', color: '#b45309', marginTop: '7px', textAlign: 'center' }}>
                  In use — remove it from the materials above before deleting.
                </div>
              )}
            </>
          )}
        </div>
      </Sidebar>
    </div>
  );
};

export default MediaLibrary;